import { useEffect } from 'react';
import { ApolloError } from '@apollo/client';
import { useRouter } from 'next/router';
// Helpers
import { getIfHaveAuthError } from 'helpers/auth';
import {
  getIsAuthHttpStatus,
  getNetworkErrorMessages,
  hasAuthExtensionsCode,
} from 'helpers/apolloAuthErrors';
// Constants
import { HOME } from 'constants/routes';

const getApolloErrorMessages = (error: ApolloError): string[] => {
  return [
    error.message,
    ...getNetworkErrorMessages(error.networkError),
    ...error.graphQLErrors.map((graphQLError) => graphQLError.message),
  ].filter((message): message is string => Boolean(message));
};

const getShouldRedirectOnError = ({
  error,
  skipTokenError,
}: {
  error?: ApolloError | boolean | string;
  skipTokenError?: boolean;
}): boolean => {
  if (!error) {
    return false;
  }

  if (!skipTokenError) {
    return true;
  }

  if (
    error instanceof ApolloError &&
    (getIsAuthHttpStatus(error.networkError) ||
      hasAuthExtensionsCode(error.graphQLErrors))
  ) {
    return false;
  }

  const errorMessages =
    typeof error === 'string'
      ? [error]
      : error instanceof ApolloError
      ? getApolloErrorMessages(error)
      : [];

  return !errorMessages.some(getIfHaveAuthError);
};

export const useRedirectOnError = ({
  error,
  redirectTo,
  skipTokenError,
}: {
  error?: ApolloError | boolean | string;
  redirectTo?: string;
  skipTokenError?: boolean;
}): null => {
  const router = useRouter();

  useEffect(() => {
    // if error is one of token/auth errors, don't redirect in order to let refresh/retry flow finish
    if (
      getShouldRedirectOnError({
        error,
        skipTokenError,
      })
    ) {
      router.push(redirectTo || HOME);
    }
  }, [error, redirectTo, router, skipTokenError]);

  return null;
};
